import { CenterSpin } from '@/components/CenterSpin';
import { EditDatasetModal, UploadDataModal } from '@/components/DatasetModals';
import { VersionHistoryModal } from '@/components/DatasetModals';
import { DetailsPageLayout } from '@/layouts/DetailsPageLayout';
import {
  getDataVersionDownloadUrl,
  useDatasetDataVersionsByDatasetId,
} from '@/queries/dataset-data-versions';
import { useDatasetSchemaVersionsByDatasetId } from '@/queries/dataset-schema-versions';
import { useDatasetById, useUpdateDataset } from '@/queries/datasets';
import {
  formatRelativeTime,
  getErrorMessage,
} from '@/utils';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DatabaseOutlined from '@ant-design/icons/DatabaseOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import HistoryOutlined from '@ant-design/icons/HistoryOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import RocketOutlined from '@ant-design/icons/RocketOutlined';
import ShareAltOutlined from '@ant-design/icons/ShareAltOutlined';
import ThunderboltOutlined from '@ant-design/icons/ThunderboltOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Col from 'antd/es/col';
import Input from 'antd/es/input';
import Modal from 'antd/es/modal';
import Popover from 'antd/es/popover';
import Row from 'antd/es/row';
import Space from 'antd/es/space';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';
import { DetailRow } from 'ui-components';

const { Text } = Typography;

const SectionContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.marginLG}px;
`;

const UppercaseLabel = styled(Typography.Text)`
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const EditActionsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.marginXS}px;
  align-items: center;
  flex-shrink: 0;
  margin-top: ${({ theme }) => theme.marginSM}px;

  .ant-btn {
    min-height: ${({ theme }) => theme.controlHeight}px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.ant-btn-sm {
      min-height: ${({ theme }) => theme.controlHeightSM}px;
      padding: ${({ theme }) => theme.paddingXXS}px
        ${({ theme }) => theme.paddingXS}px;
    }
  }
`;

const DescriptionContainer = styled.div`
  margin-top: ${({ theme }) => theme.marginXS}px;
`;

const EmptyStateIcon = styled.div`
  ${({ theme }) => `
  font-size: ${theme.fontSizeLG * 3}px;
  margin-bottom: ${theme.margin}px;
  opacity: ${theme.opacityImage * 0.3};
`}
`;

const EmptyStateSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.paddingXL}px 0;
`;

const EmptyStateTitle = styled(Typography.Title)`
  margin: 0 0 ${({ theme }) => theme.marginXS}px 0 !important;
`;

const EmptyStateDescription = styled(Typography.Text)`
  display: block;
  margin-bottom: ${({ theme }) => theme.margin}px;
`;

export const DatasetDetailPage: React.FC = () => {
  const { teamId, datasetId } = useParams<{
    teamId: string;
    datasetId: string;
  }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const theme = useTheme();
  const {
    data: dataset,
    isLoading,
    error,
    refetch: refetchDataset,
  } = useDatasetById(datasetId || null, {
    enabled: !!datasetId,
  });

  const updateDatasetMutation = useUpdateDataset();

  // Fetch schema versions
  const { data: schemaVersions, isLoading: isLoadingSchemaVersions } =
    useDatasetSchemaVersionsByDatasetId(datasetId || null, {
      enabled: !!datasetId,
    });

  // Fetch data versions
  const { data: dataVersions, isLoading: isLoadingDataVersions } =
    useDatasetDataVersionsByDatasetId(datasetId || null, {
      enabled: !!datasetId,
    });

  // Get current/active versions
  const activeSchemaVersion = schemaVersions?.items.find(
    (v) => v.status === 'current',
  );
  const activeDataVersion = dataVersions?.items.find(
    (v) => v.status === 'current',
  );

  // Edit states
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDataVersionHistoryOpen, setIsDataVersionHistoryOpen] =
    useState(false);

  // Loading states
  const [isDownloading, setIsDownloading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Get processing status from the dataset API response
  const getProcessingStatus = () => {
    // If currently processing via UI, show processing state
    if (isProcessing) {
      return 'processing';
    }

    // Use the actual processing status from the backend
    return dataset?.processingStatus || null;
  };

  const processingStatus = getProcessingStatus();

  // Poll dataset data when processing
  useEffect(() => {
    if (
      processingStatus === 'in_progress' ||
      processingStatus === 'processing'
    ) {
      const interval = setInterval(() => {
        void refetchDataset();
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(interval);
    }
  }, [processingStatus, refetchDataset]);

  const handleQueryDataset = () => {
    if (!activeDataVersion) {
      void message.warning('Please upload data first before querying.');
      return;
    }

    if (!activeSchemaVersion) {
      void message.warning(
        'Please define a schema first. This helps structure your queries.',
      );
      return;
    }

    Modal.info({
      title: 'Query Your Data',
      content: (
        <div>
          <p>The query interface for datasets is coming soon!</p>
          <p>Once ready, you'll be able to:</p>
          <ul>
            <li>
              Write SQL queries against your dataset records
            </li>
            <li>
              Filter and search through your{' '}
              {activeSchemaVersion.columnsJson?.length || 'data'} columns
            </li>
            <li>Export query results in various formats</li>
            <li>Save and share queries with your team</li>
          </ul>
          <p style={{ marginTop: '16px', color: '#666' }}>
            <strong>Processing required:</strong> Your data needs to be
            processed first for optimal query performance.
          </p>
        </div>
      ),
      onOk() {},
    });
  };

  const handleExportDataset = async (format: 'csv' | 'json' = 'csv') => {
    if (!activeDataVersion || !dataset) {
      void message.warning('No data available to export');
      return;
    }

    // For now, we'll use the existing download functionality for CSV
    // JSON export would require backend implementation
    if (format === 'json') {
      void message.info('JSON export will be available soon');
      return;
    }

    await handleDownloadData();
  };

  const handleShareDataset = async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      void message.success('Dataset URL copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy URL to clipboard');
    }
  };

  const handleEditDataset = () => {
    setIsEditModalOpen(true);
  };

  const handleProcessDataset = async () => {
    if (!dataset || !datasetId) return;

    // Check if we have both schema and data versions
    if (!activeSchemaVersion) {
      void message.warning(
        'Please define a schema first. A schema tells us how to interpret your data columns.',
      );
      return;
    }

    setIsProcessing(true);
    try {
      await updateDatasetMutation.mutateAsync({
        datasetId,
        data: { shouldProcess: true },
      });
      void message.info(
        'Processing started! This will prepare your data for fast queries and analysis.',
        4,
      );
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to start processing');
      void message.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadData = async () => {
    if (!activeDataVersion || !dataset) return;

    setIsDownloading(true);
    try {
      const { downloadUrl } = await getDataVersionDownloadUrl(
        activeDataVersion.datasetDataVersionId,
      );

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${dataset.name}-${activeDataVersion.versionNo}.${activeDataVersion.fileType}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      void message.success('Download started');
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to download data');
      void message.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  // Edit handlers
  const startEditingDescription = () => {
    if (dataset) {
      setEditedDescription(dataset.description || '');
      setIsEditingDescription(true);
    }
  };

  const cancelEditingDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  const saveDescriptionEdit = async () => {
    if (!dataset || !datasetId) return;

    try {
      await updateDatasetMutation.mutateAsync({
        datasetId,
        data: { description: editedDescription.trim() || undefined },
      });
      setIsEditingDescription(false);
      void message.success('Dataset description updated successfully');
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to update dataset description',
      );
      void message.error(errorMessage);
    }
  };

  // Copy the dataset id to the clipboard
  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(dataset?.datasetId || '');
      void message.success('ID copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy ID');
    }
  };

  // Determine layout props based on state
  const getLayoutProps = () => {
    if (!teamId || !datasetId) {
      return {
        title: 'Missing Information',
        breadcrumbs: [
          {
            label: 'Datasets',
            path: teamId ? `/team/${teamId}/datasets` : '/',
          },
          { label: 'Error' },
        ],
      };
    }

    if (isLoading || isLoadingSchemaVersions || isLoadingDataVersions) {
      return {
        title: 'Loading Dataset',
        breadcrumbs: [
          { label: 'Datasets', path: `/team/${teamId}/datasets` },
          { label: dataset?.name || 'Loading...' },
        ],
      };
    }

    if (error) {
      return {
        title: 'Error Loading Dataset',
        breadcrumbs: [
          { label: 'Datasets', path: `/team/${teamId}/datasets` },
          { label: 'Error' },
        ],
      };
    }

    if (!dataset) {
      return {
        title: 'Dataset Not Found',
        breadcrumbs: [
          { label: 'Datasets', path: `/team/${teamId}/datasets` },
          { label: 'Not Found' },
        ],
      };
    }

    return {
      title: (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: `${theme.margin}px`,
          }}
        >
          {dataset.name}
          {activeDataVersion && (
            <Tag
              color={
                activeDataVersion.status === 'current'
                  ? 'success'
                  : activeDataVersion.status === 'draft'
                    ? 'processing'
                    : 'default'
              }
            >
              {activeDataVersion.status === 'current'
                ? 'Ready'
                : activeDataVersion.status === 'draft'
                  ? 'Draft'
                  : 'Archived'}
            </Tag>
          )}
        </div>
      ),
      breadcrumbs: [
        { label: 'Datasets', path: `/team/${teamId}/datasets` },
        { label: dataset.name },
      ],
      actions: [
        {
          label: 'Query Data',
          type: 'primary' as const,
          icon: <PlayCircleOutlined />,
          onClick: handleQueryDataset,
        },
        {
          label: 'Process Dataset',
          type: 'secondary' as const,
          icon: <RocketOutlined />,
          onClick: handleProcessDataset,
          loading: isProcessing,
          disabled: !activeSchemaVersion || !activeDataVersion || isProcessing,
        },
        {
          label: 'Share dataset',
          type: 'primary' as const,
          icon: <ShareAltOutlined />,
          onClick: handleShareDataset,
        },
        {
          label: 'Edit dataset',
          type: 'primary' as const,
          icon: <EditOutlined />,
          onClick: handleEditDataset,
        },
      ],
    };
  };

  const layoutProps = getLayoutProps();

  // Render different content based on state
  const renderContent = () => {
    if (!teamId || !datasetId) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="danger">
              Unable to load dataset details. Team or dataset information is
              missing.
            </Typography.Text>
            <div style={{ marginTop: `${theme.margin}px` }}>
              <Button
                type="primary"
                onClick={() => {
                  window.location.href = teamId
                    ? `/team/${teamId}/datasets`
                    : '/';
                }}
              >
                Back to Datasets
              </Button>
            </div>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    if (isLoading || isLoadingSchemaVersions || isLoadingDataVersions) {
      return (
        <DetailsPageLayout.TopContent>
          <CenterSpin />
        </DetailsPageLayout.TopContent>
      );
    }

    if (error) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="danger">
              There was an error loading the dataset details. Please try again.
            </Typography.Text>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    if (!dataset) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="secondary">
              The dataset you're looking for could not be found.
            </Typography.Text>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    // Render success state with side content and main content
    return [
      <DetailsPageLayout.SideContent
        key="side-content"
        title="Dataset Overview"
      >
        <SectionContainer>
          <UppercaseLabel type="secondary">ABOUT THIS DATASET</UppercaseLabel>
          <DescriptionContainer>
            {isEditingDescription ? (
              <div>
                <Input.TextArea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      void saveDescriptionEdit();
                    }
                  }}
                  placeholder="Enter a description for this dataset..."
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  autoFocus
                />
                <EditActionsContainer>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={saveDescriptionEdit}
                    loading={updateDatasetMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={cancelEditingDescription}
                  >
                    Cancel
                  </Button>
                </EditActionsContainer>
              </div>
            ) : (
              <div
                style={{ cursor: 'pointer' }}
                onClick={startEditingDescription}
              >
                <Typography.Text style={{ lineHeight: '1.5' }}>
                  {dataset.description ? (
                    dataset.description
                  ) : (
                    <Typography.Text type="secondary" italic>
                      No description provided - click to add one
                    </Typography.Text>
                  )}
                </Typography.Text>
              </div>
            )}
          </DescriptionContainer>
        </SectionContainer>

        <DetailRow>
          <Typography.Text>Dataset ID</Typography.Text>
          <Tooltip title="Click to copy ID">
            <Typography.Text
              code
              style={{ cursor: 'pointer' }}
              onClick={handleCopyId}
            >
              {dataset.datasetId}
            </Typography.Text>
          </Tooltip>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Created</Typography.Text>
          <Typography.Text>
            {formatRelativeTime(dataset.createdAt)}
          </Typography.Text>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Last Updated</Typography.Text>
          <Typography.Text>
            {formatRelativeTime(dataset.updatedAt)}
          </Typography.Text>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Data Version</Typography.Text>
          <Typography.Text>
            {activeDataVersion ? `v${activeDataVersion.versionNo}` : '—'}
          </Typography.Text>
        </DetailRow>
        <DetailRow>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Fields</Typography.Text>
          <Typography.Text>
            {activeSchemaVersion?.columnsJson?.length || '—'}
          </Typography.Text>
        </DetailRow>
        <DetailRow>
        </DetailRow>
      </DetailsPageLayout.SideContent>,

      <DetailsPageLayout.SideContent
        key="side-content-actions"
        title="Export Data"
      >
        <SectionContainer>
          <Typography.Text type="secondary">
            Export the dataset for offline use or analysis.
          </Typography.Text>
          <div style={{ marginTop: `${theme.marginXS}px` }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button block onClick={() => handleExportDataset('csv')}>
                Export as CSV
              </Button>
              <Button block onClick={() => handleExportDataset('json')}>
                Export as JSON
              </Button>
            </Space>
          </div>
        </SectionContainer>
      </DetailsPageLayout.SideContent>,

      <DetailsPageLayout.MainContent
        key="main-content-details"
        title="Dataset Details"
        extra={
          activeDataVersion && (
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setIsDataVersionHistoryOpen(true)}
            >
              Version History
            </Button>
          )
        }
      >
        <SectionContainer>
          <UppercaseLabel type="secondary">DATA</UppercaseLabel>
          {activeDataVersion ? (
            <div style={{ marginTop: 8 }}>
              <DetailRow>
                <Text>FILE TYPE</Text>
                <Text>{activeDataVersion.fileType.toUpperCase()}</Text>
              </DetailRow>
              <DetailRow>
                <Text>VERSION</Text>
                <Text>v{activeDataVersion.versionNo}</Text>
              </DetailRow>
              <Space style={{ marginTop: `${theme.margin}px` }}>
                <Button onClick={handleDownloadData} loading={isDownloading}>
                  Download
                </Button>
                <Button
                  type="primary"
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  Upload New Version
                </Button>
              </Space>
            </div>
          ) : (
            <EmptyStateSection>
              <EmptyStateIcon>
                <DatabaseOutlined />
              </EmptyStateIcon>
              <EmptyStateTitle level={5}>Ready for your data!</EmptyStateTitle>
              <EmptyStateDescription type="secondary">
                Upload CSV or Excel files to get started. Our smart upload will
                automatically detect your data structure.
              </EmptyStateDescription>
              <Space>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  Smart Upload
                </Button>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  Manual Upload
                </Button>
              </Space>
            </EmptyStateSection>
          )}
        </SectionContainer>
      </DetailsPageLayout.MainContent>,

      <DetailsPageLayout.MainContent
        key="main-content-advanced"
        title="Dataset Actions"
      >
        {activeSchemaVersion && activeDataVersion && (
          <SectionContainer>
            <UppercaseLabel type="secondary">DATASET PROCESSING</UppercaseLabel>
            <div style={{ marginTop: `${theme.marginXS}px` }}>
              <Typography.Text
                type="secondary"
                style={{
                  display: 'block',
                  marginBottom: `${theme.margin}px`,
                  fontSize: `${theme.fontSizeSM}px`,
                }}
              >
                Process your dataset to apply schema transformations, data
                cleaning, and prepare it for analysis.
              </Typography.Text>
              <Button
                block
                icon={<RocketOutlined />}
                onClick={handleProcessDataset}
                loading={isProcessing}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Start Processing'}
              </Button>
            </div>
          </SectionContainer>
        )}

        <SectionContainer>
          <UppercaseLabel type="secondary">SCHEMA & STRUCTURE</UppercaseLabel>
          <div style={{ marginTop: `${theme.marginXS}px` }}>
            <Typography.Text
              type="secondary"
              style={{
                display: 'block',
                marginBottom: `${theme.margin}px`,
                fontSize: `${theme.fontSizeSM}px`,
              }}
            >
              Define and preview how your dataset is structured before analysis.
            </Typography.Text>
            <Row gutter={16}>
              <Col span={12}>
                <Button
                  block
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(`/team/${teamId}/datasets/${datasetId}/schema`)
                  }
                >
                  Edit & Review Schema
                </Button>
              </Col>
              <Col span={12}>
                <Popover
                  content="Preview Dataset functionality will be implemented soon"
                  trigger="hover"
                >
                  <Button block icon={<EyeOutlined />}>
                    Preview Data
                  </Button>
                </Popover>
              </Col>
            </Row>
          </div>
        </SectionContainer>
      </DetailsPageLayout.MainContent>,
    ];
  };

  return (
    <div>
      <DetailsPageLayout {...layoutProps}>{renderContent()}</DetailsPageLayout>
      {datasetId && dataset && teamId && (
        <>
          <EditDatasetModal
            open={isEditModalOpen}
            dataset={dataset}
            teamId={teamId}
            onCancel={() => setIsEditModalOpen(false)}
            onSuccess={() => {
              setIsEditModalOpen(false);
              // Success message is already shown by the form component
              // The React Query cache will be automatically invalidated by the mutation
            }}
          />

          <UploadDataModal
            open={isUploadModalOpen}
            onCancel={() => setIsUploadModalOpen(false)}
            datasetId={datasetId}
            onSuccess={() => {
              setIsUploadModalOpen(false);
            }}
          />

          <VersionHistoryModal
            open={isDataVersionHistoryOpen}
            onCancel={() => setIsDataVersionHistoryOpen(false)}
            title="Data Version History"
            versions={dataVersions?.items || []}
            datasetName={dataset?.name || 'Dataset'}
            loading={isLoadingDataVersions}
          />
        </>
      )}
    </div>
  );
};
